import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination
} from '@material-ui/core';
import Chart from 'react-apexcharts';
import { departments } from './lodgeData';
import { lodgeColors } from './theme';
import { Panel } from './Panel';
import { hlekweniUrl } from '../../../../utils/constants';

const Stat = ({ l, v, h }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      p: 2.5,
      background: 'linear-gradient(160deg, #FBF7EE 0%, #F2EBD8 100%)'
    }}
  >
    <Typography variant="overline" color="text.secondary" sx={{ fontSize: 11 }}>
      {l}
    </Typography>
    <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 600, mt: 1 }}>
      {v}
    </Typography>
    {h && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {h}
      </Typography>
    )}
  </Paper>
);

/* eslint-disable no-nested-ternary */
const HospitalityEventsChart = () => {
  const [deptId, setDeptId] = useState(departments[0].refId);
  const [loading, setLoading] = useState(false);
  const deptName = departments.find((d) => d.refId === deptId)?.departmentName ?? '';
  const [dataList, setDataList] = useState([
    {
      productRefId: 0,
      department: 5,
      stockNumber: '0000',
      descriptions: 'Temp',
      stockTypeId: 1,
      cost: 0.0,
      qtyReceivedTotal: 0,
      qtyIssuedTotal: 0,
      qtyInStock: 0
    }
  ]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  useEffect(() => {
    setPage(0);
  }, [deptId]);

  const items = useMemo(() => dataList.filter((i) => i.department === deptId), [dataList, deptId]);
  const pagedItems = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    getStoresInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const totalStock = items.reduce((s, i) => s + i.qtyInStock, 0);
    const totalIssued = items.reduce((s, i) => s + i.qtyIssuedTotal, 0);
    const totalReceived = items.reduce((s, i) => s + i.qtyReceivedTotal, 0);
    const totalValue = items.reduce((s, i) => s + i.qtyInStock * i.cost, 0);
    return { totalStock, totalIssued, totalReceived, totalValue, total: items.length };
  }, [items]);

  const chartOptions = {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
    colors: [lodgeColors.primary, lodgeColors.accent],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%', horizontal: false } },
    dataLabels: { enabled: false },
    grid: { borderColor: '#DCD3BD', strokeDashArray: 3 },
    xaxis: {
      categories: items.map((i) =>
        i.descriptions.length > 18 ? `${i.descriptions.slice(0, 16)}…` : i.descriptions
      ),
      labels: { style: { colors: '#5A6A5C', fontSize: '11px' }, rotate: -25 },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: { labels: { style: { colors: '#5A6A5C', fontSize: '12px' } } },
    legend: { position: 'top', horizontalAlign: 'right', fontSize: '12px' },
    tooltip: { theme: 'light' }
  };

  const chartSeries = [
    { name: 'Qty In Stock', data: items.map((i) => i.qtyInStock) },
    { name: 'Qty Issued', data: items.map((i) => i.qtyIssuedTotal) }
  ];

  const getStoresInventory = () => {
    setLoading(true);
    const url = `${hlekweniUrl}/api/booking/getStoresInventory`;
    fetch(url)
      .then((res) => res.json())
      .then(
        (result) => {
          setLoading(false);
          console.log('returned GetMonthlyNpvTotals Summary list', result.result);
          setDataList(result.result ?? []);
        },
        (error) => {
          setLoading(false);
          // notifyPopup(`Error in getChartData!: ${error}`, 'error');
        }
      );
  };

  return (
    <Stack spacing={3}>
      <Panel
        title="Stores Inventory"
        subtitle="Filter by department to view current stock levels"
        action={
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="dept-select-label">Department</InputLabel>
            <Select
              labelId="dept-select-label"
              label="Department"
              value={deptId}
              onChange={(e) => setDeptId(Number(e.target.value))}
            >
              {departments.map((d) => (
                <MenuItem key={d.refId} value={d.refId}>
                  {d.departmentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3
          }}
        >
          <Stat l="Tracked Items" v={String(totals.total)} h={deptName} />
          <Stat l="Qty In Stock" v={String(totals.totalStock)} h="Across all items" />
          <Stat l="Qty Issued" v={String(totals.totalIssued)} h="Total issued to date" />
          <Stat l="Stock Value" v={`$ ${totals.totalValue.toFixed(2)}`} h="Cost × on-hand" />
        </Box>

        <Box sx={{ mb: 1 }}>
          <Chart options={chartOptions} series={chartSeries} type="bar" height={300} />
        </Box>
      </Panel>

      <Panel
        title={`${deptName} — Inventory Levels`}
        subtitle="Cost, received, issued and current stock"
      >
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  'Stock #',
                  'Description',
                  'Cost',
                  'Qty Received',
                  'Qty Issued',
                  'Qty In Stock',
                  'Stock Value'
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontSize: 11,
                      color: 'text.secondary',
                      fontWeight: 500
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedItems.map((i) => (
                <TableRow key={i.productRefId} hover>
                  <TableCell
                    sx={{
                      fontFamily: 'ui-monospace, monospace',
                      fontSize: 12,
                      color: 'text.secondary'
                    }}
                  >
                    {i.stockNumber}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{i.descriptions}</TableCell>
                  <TableCell>$ {i.cost.toFixed(2)}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{i.qtyReceivedTotal}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{i.qtyIssuedTotal}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{i.qtyInStock}</TableCell>
                  <TableCell>$ {(i.qtyInStock * i.cost).toFixed(2)}</TableCell>
                </TableRow>
              ))}
              {pagedItems.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}
                  >
                    No inventory items for this department.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={items.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Panel>
    </Stack>
  );
};

export default HospitalityEventsChart;
